// Moeda em reais: leitura tolerante do que o usuário digita, formatação e soma
// exata em centavos. Módulo puro (sem React nem DOM), usado pelo CurrencyCell,
// pelo rodapé do grupo (GroupFooter) e pela barra de seleção. O parser tem uma
// cópia no backend (backend/src/utils/currencyText.js, usada na conversão de
// tipo de coluna); backend/test/unit/currency.test.js roda os mesmos casos nas duas.

// Casas decimais vêm de column.config.decimals; fora de 0..6 vale o padrão.
const DEFAULT_DECIMALS = 2;
const MAX_DECIMALS = 6;

function normalizeDecimals(decimals) {
  return Number.isInteger(decimals) && decimals >= 0 && decimals <= MAX_DECIMALS ? decimals : DEFAULT_DECIMALS;
}

// Só número finito é valor de moeda. String, objeto, boolean e NaN ficam de
// fora: sumCurrency ignora, formatBRL devolve texto vazio.
function isAmount(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

// Cabe em centavos inteiros sem perder precisão (sumCurrency soma em centavos).
function fitsInCents(n) {
  return Number.isSafeInteger(Math.round(Math.abs(n) * 100));
}

// "1.234.567" ou "1,234,567": primeiro grupo de 1 a 3 dígitos (sem zero à
// esquerda) e os demais de exatamente 3.
function isThousandsGrouped(text, sep) {
  const [first, ...rest] = text.split(sep);
  return /^[1-9]\d{0,2}$/.test(first) && rest.every((g) => /^\d{3}$/.test(g));
}

// `body` só tem dígitos, pontos e vírgulas (e pelo menos um dígito).
function parseUnsignedDecimal(body) {
  const commas = body.split(',').length - 1;
  const dots = body.split('.').length - 1;
  let int = body;
  let frac = '';

  if (commas && dots) {
    // Os dois presentes: o ÚLTIMO é o decimal ("1.234,56" ou "1,234.56"); o outro só pode ser milhar.
    const cut = Math.max(body.lastIndexOf(','), body.lastIndexOf('.'));
    const decimal = body[cut];
    const thousand = decimal === ',' ? '.' : ',';
    const head = body.slice(0, cut);
    if (head.includes(decimal) || !isThousandsGrouped(head, thousand)) return null;
    int = head.split(thousand).join('');
    frac = body.slice(cut + 1);
  } else if (commas) {
    if (commas === 1) {
      [int, frac] = body.split(',');
    } else {
      if (!isThousandsGrouped(body, ',')) return null;
      int = body.split(',').join('');
    }
  } else if (dots) {
    if (dots > 1) {
      if (!isThousandsGrouped(body, '.')) return null;
      int = body.split('.').join('');
    } else {
      const [head, tail] = body.split('.');
      // Ponto único com 3 dígitos depois é milhar ("1.234" = 1234, convenção
      // pt-BR); o resto é decimal ("1234.56", "12.5", "0.125").
      if (tail.length === 3 && /^[1-9]\d{0,2}$/.test(head)) int = head + tail;
      else { int = head; frac = tail; }
    }
  }
  return Number(`${int || '0'}.${frac || '0'}`);
}

/**
 * Lê o que o usuário digita numa célula de moeda. Aceita "1.234,56", "1234,56",
 * "R$ 1.234,56", "1,234.56" e "1234.56"; negativo com "-" antes ou depois,
 * "-R$ 1.234,56", "R$ -1.234,56" ou entre parênteses, "(1.234,56)". Devolve
 * null para vazio e para o que não é número — quem chama decide se isso apaga
 * o valor ou mantém o anterior.
 *
 * Duas escolhas que não são óbvias:
 *  - Com os dois separadores, o ÚLTIMO é o decimal. Com um só, a vírgula é
 *    sempre decimal e o ponto é milhar quando bem formado ("1.234" = 1234).
 *  - Milhar malformado ("1.23.4,5") é recusado em vez de adivinhado: em
 *    dinheiro, ler errado em silêncio custa mais que pedir de novo.
 *
 * @param {string} text
 * @returns {number|null}
 */
export function parseCurrencyInput(text) {
  if (typeof text === 'number') return isAmount(text) && fitsInCents(text) ? text : null;
  if (typeof text !== 'string') return null;

  let s = text
    .replace(/[−–]/g, '-') // menos tipográfico, comum em texto colado de planilha
    .replace(/R\$/gi, '')
    .replace(/[\s  ]/g, '');

  let negative = false;
  if (s.startsWith('(') && s.endsWith(')')) { negative = true; s = s.slice(1, -1); }
  if (s.startsWith('-')) { negative = true; s = s.slice(1); } else if (s.startsWith('+')) s = s.slice(1);
  if (s.endsWith('-')) { negative = true; s = s.slice(0, -1); }

  let n;
  const sci = s.match(/^(\d+(?:[.,]\d+)?)e([+-]?\d+)$/i); // o que String(1e-7) produz
  if (sci) n = Number(`${sci[1].replace(',', '.')}e${sci[2]}`);
  else if (/^[\d.,]*\d[\d.,]*$/.test(s)) n = parseUnsignedDecimal(s);
  else return null;

  if (n === null || !Number.isFinite(n) || !fitsInCents(n)) return null;
  return negative && n !== 0 ? -n : n;
}

// Um formatador por quantidade de casas: criar Intl.NumberFormat a cada célula
// renderizada é caro numa tabela grande.
const digitFormatters = new Map();
function digitFormatter(decimals) {
  let f = digitFormatters.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    digitFormatters.set(decimals, f);
  }
  return f;
}

/**
 * 1234.56 -> "R$ 1.234,56"; -1234.56 -> "-R$ 1.234,56". null, undefined, NaN e
 * qualquer valor que não seja número -> "". O sinal e o "R$" são montados aqui
 * (não pelo Intl) para o texto sair igual em qualquer navegador e para que um
 * valor que arredonda para zero apareça como "R$ 0,00", nunca "-R$ 0,00".
 *
 * @param {number|null|undefined} value
 * @param {number} [decimals=2] casas decimais; fora de 0..6 vale 2
 * @returns {string}
 */
export function formatBRL(value, decimals = 2) {
  if (!isAmount(value)) return '';
  const digits = digitFormatter(normalizeDecimals(decimals)).format(Math.abs(value));
  const sign = value < 0 && /[1-9]/.test(digits) ? '-' : '';
  return `${sign}R$ ${digits}`;
}

// Meio centavo arredonda para longe do zero: Math.round puro manda -0,5 para 0
// e +0,5 para 1, e então +x e -x deixariam de se anular.
function roundAwayFromZero(x) {
  return x < 0 ? -Math.round(-x) : Math.round(x);
}

/**
 * Soma algébrica (negativo subtrai) em centavos inteiros, para não acumular
 * erro de ponto flutuante (0,1 + 0,2). Ignora null, undefined, NaN e tudo que
 * não é número. `decimals` só importa acima de 2: colunas com mais casas somam
 * em escala maior, para não arredondar cada parcela para o centavo.
 *
 * @param {Iterable<number|null|undefined>} values
 * @param {number} [decimals=2]
 * @returns {number}
 */
export function sumCurrency(values, decimals = 2) {
  const scale = 10 ** Math.max(2, normalizeDecimals(decimals));
  let total = 0;
  for (const v of values ?? []) {
    if (isAmount(v)) total += roundAwayFromZero(v * scale);
  }
  return total / scale;
}
